import { BEO_STATE } from '../sim/beo.js';
import { buildDiagram } from './roomDiagram.js';
import { formatSpan, formatTime } from '../sim/clock.js';
import { LINENS } from '../data/objects.js';
import { SHORTFALLS } from '../data/resources.js';
import { byId as departmentsById } from '../data/departments.js';
import { formatSpan as span } from '../sim/clock.js';

/**
 * The physical BEO board in the muster alcove, as a panel.
 *
 * The room diagram is the whole design of this screen. A banquet event order is
 * a sheet of numbers plus a picture of the room, and the picture is what a
 * server actually reads — nobody sets six rounds from the sentence "three
 * across, two deep, in one from the corner". So the grid is drawn as the
 * ballroom's real anchor grid, with the tables this event wants marked on it
 * and the dance floor hatched out, and that diagram is the thing the player
 * carries in their head to the ballroom.
 *
 * Which is also why the panel is not the tutorial. Nothing here highlights the
 * anchors in the world. Reading the diagram, and remembering it, is the job.
 *
 * Mouse-driven, so pointer lock is released while it is open — hence the
 * caller pausing the sim. A board you have to read while a deadline runs is a
 * board nobody reads.
 */
export function createBeoPanel({ parent, grid, onAccept, onSit, onClose }) {
  const root = document.createElement('div');
  root.className = 'board';
  root.hidden = true;

  const head = document.createElement('div');
  head.className = 'board-head';
  head.innerHTML = `<h2>BEO BOARD</h2><span class="board-sub"></span>
    <button class="board-close" type="button">Close  [Esc]</button>`;

  const list = document.createElement('div');
  list.className = 'board-list';

  root.append(head, list);
  parent.append(root);

  head.querySelector('.board-close').addEventListener('click', () => close());

  function close() {
    root.hidden = true;
    onClose?.();
  }

  function card(entry, clock, canAccept, describe, whose, pace = 1) {
    const { beo, times, state, record, live, remaining } = entry;
    const node = document.createElement('article');
    node.className = 'card';
    node.dataset.state = state;

    const linen = beo.setup ? LINENS[beo.setup.linen] : null;

    const spec = document.createElement('dl');
    spec.className = 'card-spec';
    /*
     * A strike is not a room, so it has no diagram and no linen: it has what is
     * out there, where it goes, and when by. The card carries those as lines.
     */
    const rows = !beo.setup
      ? [...(beo.lines ?? []), ['Due by', formatTime(times.setupBy)]]
      : null;
    spec.innerHTML = (rows ?? [
      ['Room', beo.room === 'ballroom' ? 'Crown Ballroom' : beo.room === 'pool' ? 'Sunstone Pool deck' : beo.room],
      ['Covers', String(beo.covers)],
      ['Tables', `${beo.setup.tableCount} × 72" round`],
      ['Chairs', `${beo.setup.chairsPerTable} per table (${beo.setup.tableCount * beo.setup.chairsPerTable} total)`],
      ['Linen', linen ? linen.name : beo.setup.linen],
      ['Set by', formatTime(times.setupBy)],
      ['Doors', formatTime(times.doors)],
    ])
      .map(([label, value]) => `<dt>${label}</dt><dd>${value}</dd>`)
      .join('');

    const staging = document.createElement('ul');
    staging.className = 'card-staging';
    staging.innerHTML = (beo.setup?.staging ?? []).map((line) => `<li>${line}</li>`).join('');

    /*
     * What the event is short of, who is holding it, and where it will land.
     *
     * Before accepting, not after. The whole decision this board asks for is
     * "which of these am I prepared to owe somebody for", and a shortfall you
     * only discover once you are committed is not a decision, it is a gotcha.
     *
     * The third column is the same one the sheet carries and it is the only
     * thing in the game that tells you where you stand with anybody. It never
     * says so: it names a place, and the place is nearer when they think well
     * of you. A card that says Kitchen and "the kitchen pass" is a hundred and
     * eighty-nine feet you have not earned your way out of yet; one that says
     * "the chair shelf, no fetch" is a department that has stopped making you
     * walk. `shortfallLine` is supplied rather than computed here, because what
     * a rung is belongs to the simulation and not to a panel.
     */
    const short = document.createElement('ul');
    short.className = 'card-short';
    short.innerHTML = (beo.shortfalls ?? [])
      .map((entry) => {
        const line = describe
          ? describe(entry)
          : {
              label: SHORTFALLS[entry.id].label,
              holderName: departmentsById[SHORTFALLS[entry.id].owner].name,
              lands: null,
              fetch: false,
              resolved: entry.resolved,
            };
        const amount = entry.amount ? ` ×${entry.amount}` : '';
        return `<li data-solved="${entry.resolved ? 'yes' : 'no'}">
          <span>${SHORTFALLS[entry.id].label}${amount}</span>
          <b>${line.holderName}</b>
          ${line.lands ? `<em data-fetch="${line.fetch ? 'yes' : 'no'}">${line.lands}</em>` : ''}
        </li>`;
      })
      .join('');

    const header = document.createElement('header');
    header.innerHTML = `<span class="card-code">${beo.code}</span>
      <h3>${beo.title}</h3>
      <span class="card-client">${beo.client}</span>`;
    /*
     * Whose room it is, and whether it is one the story wants you in. The
     * department a room belongs to is who it wins over, and the board never
     * said: a player in the second act choosing between six identical-looking
     * cards had no way to know which of them the Kitchen cared about.
     */
    const owner = whose?.(beo);
    if (owner) {
      const tag = document.createElement('p');
      tag.className = 'card-owner';
      tag.dataset.wins = owner.wins ? 'yes' : 'no';
      tag.textContent = owner.wins ? `${owner.name}'s room · wins over ${owner.them}` : `${owner.name}'s room`;
      header.append(tag);
    }

    const foot = document.createElement('footer');
    const status = document.createElement('span');
    status.className = 'card-status';

    if (state === BEO_STATE.GRADED) {
      status.textContent = `Filed · grade ${record.grade}`;
      status.dataset.tone = 'good';
    } else if (state === BEO_STATE.MISSED) {
      status.textContent = 'Filed · never set';
      status.dataset.tone = 'bad';
    } else if (state === BEO_STATE.ACCEPTED) {
      status.textContent = `Yours · ${formatSpan(remaining)} to set`;
      status.dataset.tone = remaining < 60 ? 'warn' : 'good';
    } else if (!live) {
      status.textContent = `Posts ${formatTime(times.posted)}`;
      status.dataset.tone = 'idle';
    } else if (remaining <= 0) {
      status.textContent = 'Deadline gone';
      status.dataset.tone = 'bad';
    } else {
      status.textContent = `${formatSpan(remaining)} to set`;
      status.dataset.tone = remaining < 120 ? 'warn' : 'idle';
    }
    foot.append(status);

    if (state === BEO_STATE.POSTED && live && remaining > 0) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'card-accept';
      button.textContent = 'Accept';
      /*
       * Why not, on the card, rather than as a line that appears after you have
       * already pressed it.
       *
       * `canAccept` is a reason or nothing: null means take it. Your supervisor
       * keeping you off the long rooms has to be readable from the board — the
       * whole rule is that you can see where you stand — and a disabled button
       * with no explanation is the opposite of that.
       */
      const refusal = canAccept(beo);
      button.disabled = Boolean(refusal);
      if (refusal) button.title = refusal;
      button.addEventListener('click', () => onAccept(beo));
      foot.append(button);
      // Said on the card as well: a tooltip never shows on a controller, and
      // a greyed button with no reason read as the board being broken.
      if (refusal) {
        const why = document.createElement('p');
        why.className = 'card-refusal';
        why.textContent = refusal;
        foot.append(why);
      }
    }

    const note = document.createElement('p');
    note.className = 'card-note';
    note.textContent = beo.note ?? '';

    /*
     * The cost of accepting, stated.
     *
     * An estimate of the work against the time left before it is due. A board
     * that over-subscribes on purpose has to show its arithmetic, or the player
     * is choosing between seven identical-looking cards and finding out four
     * hours later which one was impossible.
     */
    const budget = document.createElement('div');
    budget.className = 'card-budget';
    const left = times.setupBy - clock.minutes;
    /*
     * At your pace, as well as at the board's. The estimate is for somebody
     * who knows the building and pushes a cart; a new starter on foot takes
     * twice that, and a card that said "fits" to them was a write-up. `pace`
     * is how long your own rooms have been taking against their estimates.
     */
    const yours = beo.estimate ? Math.round(beo.estimate * pace) : 0;
    const fits = beo.estimate ? left >= yours : true;
    budget.dataset.fits = fits ? 'yes' : 'no';
    budget.innerHTML = beo.estimate
      ? `<span>≈ ${span(beo.estimate)} of work${pace > 1.15 ? `, about ${span(yours)} at your pace` : ''}</span><b>${
          left > 0 ? `${span(left)} until due` : 'overdue'
        }</b>${left > 0 && !fits ? '<em>Not enough time for you to set this one</em>' : ''}`
      : '';

    node.append(
      header,
      spec,
      ...(beo.setup ? [buildDiagram(beo.setup, beo.grid ?? grid)] : []),
      staging,
      ...((beo.shortfalls ?? []).length ? [short] : []),
      budget,
      note,
      foot
    );
    return node;
  }

  /**
   * A class slot, pinned above the events.
   *
   * Above, because it is the shorter-lived thing on the board and because a
   * slot you scrolled past is a slot you missed. It is drawn in the same card
   * shape as a BEO and deliberately does not shout: the whole contract with the
   * player is that a class is optional, and a slot rendered as a demand would
   * be lying to them about that.
   *
   * The five pips are the point of the card. What a class is worth is "four
   * more of these to go", and a progress bar says that in less space than a
   * sentence and without a number nobody can convert into anything.
   */
  function slotCard(slot, sit) {
    const { entry, state, level, row, passed } = slot;
    const node = document.createElement('article');
    /*
     * Its own class name, and not `card`.
     *
     * It looks like a card and it is posted on the same wall, but `.card` is a
     * BEO on this board and half a dozen headless scripts read it that way:
     * count the cards, take the first one, press its Accept. Dressing a class
     * slot in the same class name quietly turned "accept the first event on
     * the board" into a null dereference in four different scripts, which is
     * the kind of thing a shared class name in a DOM is always one commit away
     * from doing.
     */
    node.className = 'slot';
    node.dataset.state = state;

    const header = document.createElement('header');
    header.innerHTML = `<span class="slot-code">CLASS</span>
      <h3>${entry.name}</h3>
      <span class="slot-where">${entry.where}</span>`;

    const pips = document.createElement('div');
    pips.className = 'slot-pips';
    pips.innerHTML = Array.from(
      { length: 5 },
      (_, i) => `<i data-on="${i < passed ? 'yes' : 'no'}"></i>`
    ).join('');

    const note = document.createElement('p');
    note.className = 'slot-note';
    note.textContent = entry.blurb;

    const foot = document.createElement('footer');
    const status = document.createElement('span');
    status.className = 'slot-status';
    if (state === 'complete') {
      status.textContent = 'Passed all five';
      status.dataset.tone = 'good';
    } else if (state === 'open') {
      status.textContent = `Level ${level} · ${span(slot.closesIn)} left`;
      status.dataset.tone = slot.closesIn < 30 ? 'warn' : 'good';
    } else if (state === 'sat') {
      status.textContent = 'Sat today. Again tomorrow.';
      status.dataset.tone = 'idle';
    } else if (state === 'before') {
      status.textContent = `Level ${level} · opens in ${span(slot.opensIn)}`;
      status.dataset.tone = 'idle';
    } else if (state === 'gone') {
      status.textContent = 'Slot gone for today';
      status.dataset.tone = 'idle';
    } else {
      status.textContent = 'Clock in first';
      status.dataset.tone = 'idle';
    }
    foot.append(status);

    if (state === 'open' && sit) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'slot-sit';
      button.textContent = `Sit level ${level}`;
      button.title = `Pass at ${
        entry.game === 'vacuum' ? `${Math.round(row.target * 100)}% of the floor` : row.target
      }`;
      button.addEventListener('click', () => sit(entry.id));
      foot.append(button);
    }

    node.append(header, pips, note, foot);
    return node;
  }

  return {
    root,
    get open() {
      return !root.hidden;
    },
    close,

    show(entries, clock, {
      /** A function from an event to why you may not take it, or null. */
      canAccept = () => null,
      subtitle = '',
      load = null,
      shortfallLine = null,
      /** Today's class slots, or an empty list on a day with none. */
      classes = [],
      /** Whose room an event is: `{ name, them, wins }`, or null. */
      whose = null,
      /** How long your rooms take against their estimates: 1 is the board's pace. */
      pace = 1,
    } = {}) {
      head.querySelector('.board-sub').textContent = load
        ? `${subtitle}   ·   ${entries.length} posted, about ${Math.round(
            load.work / 60
          )}h of work against a ${Math.round(load.shiftMinutes / 60)}h shift`
        : subtitle;
      list.replaceChildren(
        ...classes.map((slot) => slotCard(slot, onSit)),
        ...(entries.length
          ? entries.map((entry) => card(entry, clock, canAccept, shortfallLine, whose, pace))
          : [empty('Nothing on the board for today.')])
      );
      root.hidden = false;
    },
  };
}

function empty(text) {
  const node = document.createElement('p');
  node.className = 'board-empty';
  node.textContent = text;
  return node;
}
